/* eslint-disable @typescript-eslint/naming-convention, @next/next/no-img-element */
import { Box } from '@packages/shared';
import type { SxProps, Theme } from '@mui/material';
import { CmsImageItem } from './CmsImageItem';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';

import type { CmsImageType } from './types';

type CmsImageProps = {
  data: CmsImageType;
  sx?: SxProps<Theme>;
};

// Helper function to convert alignment string to its corresponding CSS flex property value
const styleJustifyContent = (Align: string) => {
  switch (Align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'unset';
  }
};

/**
 * CmsImage component to render image content fetched from the CMS.
 *
 * @param data containing image data and optional fields.
 * @param sx optional sx props
 * @returns JSX.Element A Box component containing the image or a linked image.
 */
export const CmsImage = ({ data, sx }: CmsImageProps) => {
  const { field_alignment: Align = 'left', field_custom_id: dynamicYieldId } = data;

  return (
    <Box
      className="image"
      data-dy={dynamicYieldId}
      component="span"
      sx={{
        display: 'flex',
        position: 'relative',
        ...(Align && {
          justifyContent: styleJustifyContent(Align),
        }),
        ...sx,
      }}
    >
      <CmsImageItem data={data} />
    </Box>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.SvgFile]: CmsImage,
  [ComponentTypes.ImageSimple]: CmsImage,
  [ComponentTypes.ImageSimpleParagraph]: CmsImage,
});
